import { ShoppingCartIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { Box, Button, Checkbox, Divider, Stack, Typography } from "@mui/joy";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ProductCarousel from "../../../components/carousel/ProductCarousel";
import { theme } from "../../../components/Theme";
import { t } from "i18next";
import { CatalogType } from "../../../types/catalog";
import { CatalogInventoryItemType } from "../../../types/item";
import { CartItemDataType } from "../../../types/cart";
import { calculatePrice, getConversionRate } from "../../../utils/cart";
import { formatPrice } from "../../../utils/formatPrice";
import { getThumbnailUrl } from "../../../utils/items";

type UOMType = "uom1" | "uom2" | "uom3";

type Props = {
  item: CatalogInventoryItemType;
  catalog?: CatalogType;
  mode?: "inventory" | "supplier";
  displayType?: "grid" | "list";
  onClick?: () => void;
  handleRemoveItem?: (id: string) => void;
  setToCart?: Dispatch<SetStateAction<CartItemDataType[]>>;
  setCartUpdated?: Dispatch<SetStateAction<boolean>>;
  handleUpdateCart?: (
    catalogId: string,
    uom: UOMType,
    item: CatalogInventoryItemType
  ) => void;
  handleUpdateOrder?: (
    orderId: string,
    catalog: CatalogType,
    item: CatalogInventoryItemType,
    quantity: number,
    uom: UOMType
  ) => void;
  resetFlag?: boolean;
  handleChecked?: (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => void;
  isSelected?: boolean;
};

/**
 * ProductCardBase - A reusable product card component that can be used for
 * both supplier catalog views and inventory management.
 */
export function ProductCardBase({
  item,
  catalog,
  mode = "inventory",
  displayType = "grid",
  onClick,
  handleRemoveItem,
  setToCart,
  setCartUpdated,
  handleUpdateCart,
  handleUpdateOrder,
  handleChecked,
  isSelected = false,
}: Props) {
  const navigate = useNavigate();
  const { catalogId } = useParams();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order");
  const isGuestMode = window.location.pathname.startsWith("/public");

  const [activeUOM, setActiveUOM] = useState<UOMType>("uom1");
  const [uomIndex, setUOMIndex] = useState(0);

  const currency =
    catalog?.currency || item.currency || item.inventoryItem.currency;

  const imageData = item.inventoryItem.images?.data;
  const images = imageData?.length
    ? imageData.map((image) => image.url)
    : [getThumbnailUrl(item.inventoryItem)];

  const uoms = [
    item.inventoryItem.uom1,
    item.inventoryItem.uom2 && item.inventoryItem.uom2,
    item.inventoryItem.uom3 && item.inventoryItem.uom3,
  ].filter(Boolean);

  useEffect(() => {
    if (setToCart) {
      setToCart((prev) => {
        const currentIndex = prev.findIndex((p) => p.cid === item.id);
        if (currentIndex !== -1) {
          const updatedItem = {
            ...prev[currentIndex],
            uom: activeUOM,
            initPrice: calculatePrice(item, activeUOM),
          };

          return [
            ...prev.slice(0, currentIndex),
            updatedItem,
            ...prev.slice(currentIndex + 1),
          ];
        }
        return prev;
      });
    }
  }, [activeUOM, item, setToCart]);

  const goToDetail = (id: string) => {
    if (isGuestMode) {
      navigate(`/public/suppliers/${catalogId}/items/detail/${id}`);
      return;
    }

    if (mode === "supplier") {
      navigate(
        `/client/suppliers/${catalogId}/items/detail/${id}?inventoryId=${
          item.inventoryItem.id
        }${orderId ? `&order=${orderId}` : ""}`
      );
    } else {
      navigate(
        `/client/catalogues/${catalogId}/items/${id}?inventoryId=${item.id}`
      );
    }
  };

  const handleChangeUOM = (val: number) => {
    if (val < 0 || val >= uoms.length) return;

    setUOMIndex(val);
    switch (val) {
      case 1:
        setActiveUOM("uom2");
        break;
      case 2:
        setActiveUOM("uom3");
        break;
      default:
        setActiveUOM("uom1");
        break;
    }
  };

  const handleAddToCartOrOrder = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (orderId && handleUpdateOrder && catalog) {
      handleUpdateOrder(orderId, catalog, item, 1, activeUOM);
    } else if (handleUpdateCart && catalogId) {
      handleUpdateCart(catalogId, activeUOM, item);
    }

    setCartUpdated?.(true);
  };

  const ProductUOMSelector = ({ type }: { type: "slide" | "buttons" }) => {
    if (type === "slide") {
      return (
        <Stack
          direction={"column"}
          sx={{
            gap: "0.5rem",
          }}
        >
          <Stack
            direction={"row"}
            className="overflow-hidden border border-gray-400 rounded-lg"
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleChangeUOM(uomIndex - 1);
              }}
              disabled={uomIndex === 0}
              className={`w-[20%] h-[34px] flex justify-center items-center border-r border-gray-700 ${
                uomIndex === 0 ? "bg-gray-200" : ""
              }`}
            >
              <ChevronLeftIcon className="w-6" />
            </button>
            <Typography level="title-md">{uoms[uomIndex]}</Typography>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleChangeUOM(uomIndex + 1);
              }}
              disabled={uomIndex === uoms.length - 1 || uoms.length === 0}
              className={`w-[20%] h-[34px] flex justify-center items-center border-l border-gray-700 ${
                uomIndex === uoms.length - 1 || uoms.length === 0
                  ? "bg-gray-200"
                  : ""
              }`}
            >
              <ChevronRightIcon className="w-6" />
            </button>
          </Stack>
          <Typography fontSize={12} textColor={"#737373"}>
            {activeUOM === "uom1"
              ? `1 ${item.inventoryItem.uom1 || "unit"}`
              : `${getConversionRate(activeUOM, item.inventoryItem)} ${
                  item.inventoryItem.uom1
                }/${item.inventoryItem[activeUOM]}`}
          </Typography>
        </Stack>
      );
    }

    if (type === "buttons") {
      return (
        <Stack
          direction={"column"}
          sx={{
            width: "100%",
            alignItems: "baseline",
            gap: "0.5rem",
            my: "1.5rem",
          }}
        >
          <Stack direction={"row"} justifyContent={"flex-start"} spacing="10px">
            {uoms.map((uom, index) => {
              if (!uom) return null;

              return (
                <Button
                  key={index}
                  variant="linkz-outlined-primary"
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleChangeUOM(index);
                  }}
                  className={`${
                    uomIndex === index
                      ? "bg-[#CFE5DA] border-[#CFE5DA] text-black"
                      : ""
                  }`}
                >
                  {uom || "unit"}
                </Button>
              );
            })}
          </Stack>

          <Typography fontSize={16} fontWeight={500} textColor={"#101828"}>
            {`${currency} ${formatPrice(calculatePrice(item, activeUOM))}`}
          </Typography>

          <Typography fontSize={12} fontWeight={500}>
            {activeUOM === "uom1"
              ? `1 ${item.inventoryItem.uom1 || "unit"}`
              : `${getConversionRate(activeUOM, item.inventoryItem)} ${
                  item.inventoryItem.uom1
                }/${item.inventoryItem[activeUOM]}`}
          </Typography>
        </Stack>
      );
    }

    return null;
  };

  // Render List View (only for supplier mode)
  if (mode === "supplier" && displayType === "list") {
    return (
      <>
        <Stack
          direction={"row"}
          onClick={() => (onClick ? onClick() : goToDetail(item.id))}
          key={item.id}
          sx={{
            borderWidth: "1px",
            boxShadow: { xs: 1, md: "none" },
            borderColor: "transparent",
            borderRadius: "1rem",
            overflow: "hidden",
            height: "100%",
            alignItems: "center",
            p: "1rem",
            gap: "1rem",
            "&:hover": {
              borderColor: theme.palette.primary[500],
              boxShadow: 3,
              backgroundColor: theme.palette.primary[50],
              cursor: "pointer",
            },
          }}
          className="transition-all"
        >
          <Stack
            direction={"column"}
            sx={{
              width: { xs: "50%", lg: "30%" },
              justifyContent: { xs: "space-between", lg: "center" },
              alignItems: "left",
              gap: "0.5rem",
            }}
          >
            <Typography
              level="text-xs-regular"
              textColor={theme.palette.neutral[500]}
              // sx={{ fontSize: "xs", color: "#737373" }}
            >
              {item.inventoryItem.sku}
            </Typography>
            <Typography
              level="text-sm-bold"
              // sx={{ fontSize: { xs: "sm", lg: "md" } }}
            >
              {item.inventoryItem.productName}
            </Typography>

            <Typography
              level="text-sm-bold"
              textColor={theme.palette.primary[500]}
            >
              {`${currency} ${formatPrice(calculatePrice(item, activeUOM))}`}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              maxWidth: "30%",
            }}
          >
            <ProductUOMSelector type="buttons" />
          </Box>

          <Stack
            direction={"column"}
            sx={{
              justifyContent: "center",
              ml: "auto",
              gap: { xs: "0.5rem", lg: 0 },
            }}
          >
            <Box
              sx={{
                display: { xs: "block", lg: "none" },
              }}
            >
              <ProductUOMSelector type="slide" />
            </Box>

            {handleUpdateCart && (
              <Button
                sx={{
                  borderRadius: "0.5rem",
                  width: "11rem",
                }}
                onClick={handleAddToCartOrOrder}
              >
                <ShoppingCartIcon
                  style={{
                    width: 18,
                    height: 18,
                    marginRight: "0.5rem",
                  }}
                />
                {t(`catalog.${orderId ? "addToOrderBtn" : "addToCartBtn"}`)}
              </Button>
            )}
          </Stack>
        </Stack>

        <hr className="hidden my-4 border border-dashed w-full lg:block" />
      </>
    );
  }

  // Default Grid View (used for both modes)
  return (
    <Stack
      direction={"column"}
      onClick={() => goToDetail(item.id)}
      key={item.id}
      sx={{
        borderWidth: "2px",
        boxShadow: { xs: 1, md: "none" },
        borderColor: "#EAECF0",
        borderRadius: "12px",
        overflow: "hidden",
        height: "100%",
        // maxWidth: { xs: "176px", lg: "208px" },
        p: 0,
        gap: "16px",
        "&:hover": {
          borderColor: "#6BAB8D",
          boxShadow: 3,
        },
      }}
      className="transition-all"
    >
      <Stack
        direction={"column"}
        sx={{
          position: "relative",
          height: "100%",
        }}
      >
        <ProductCarousel
          alt={item.inventoryItem.sku}
          slides={images}
          businessId={item.inventoryItem.business ?? ""}
          onClick={() =>
            goToDetail(mode === "supplier" ? item.id : item.inventoryItem.id)
          }
        />

        {/* Selection checkbox (inventory mode) */}
        {mode === "inventory" && handleChecked && (
          <Checkbox
            size="lg"
            checked={isSelected}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              e.stopPropagation();
              handleChecked(e, item.id);
            }}
            sx={{
              position: "absolute",
              top: "1.25rem",
              left: "1.25rem",
            }}
          />
        )}

        {/* Remove button (inventory mode) */}
        {mode === "inventory" && handleRemoveItem && (
          <TrashIcon
            onClick={(event) => {
              event.stopPropagation();
              handleRemoveItem(item.id);
            }}
            className="h-6 w-6 text-danger-500 bg-white p-[2px] rounded cursor-pointer absolute top-[1.25rem] right-[1.25rem]"
          />
        )}

        <Box
          sx={{
            p: "16px",
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Stack
            direction={"column"}
            sx={{
              gap: "0.25rem",
            }}
          >
            <Typography
              level="text-xs-regular"
              textColor={theme.palette.neutral[500]}
            >
              {item.inventoryItem.sku}
            </Typography>

            <Typography
              level="text-sm-bold"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {item.inventoryItem.productName}
            </Typography>
          </Stack>

          <Divider
            sx={{
              width: "100%",
              my: "16px",
              backgroundColor: theme.palette.neutral[300],
            }}
          />

          {/* UOM Selector */}
          <ProductUOMSelector type="slide" />

          {/* Price Display */}
          <Box sx={{ pt: "0.5rem" }}>
            <Typography fontWeight={700} fontSize={16} textColor={"#408565"}>
              {currency} {formatPrice(calculatePrice(item, activeUOM))}
            </Typography>
          </Box>

          {/* Add to Cart Button (supplier mode only) */}
          {mode === "supplier" && handleUpdateCart && (
            <Button
              fullWidth
              sx={{
                borderRadius: "8px",
                mt: "16px",
              }}
              onClick={handleAddToCartOrOrder}
            >
              <ShoppingCartIcon
                style={{
                  width: 18,
                  height: 18,
                  marginRight: "8px",
                }}
              />
              {t(`catalog.${orderId ? "addToOrderBtn" : "addToCartBtn"}`)}
            </Button>
          )}
        </Box>
      </Stack>
    </Stack>
  );
}
